package com.patronat.top.ui.molecules.inputs

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.graphics.takeOrElse
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.patronat.top.ui.atoms.inputs.InputLabel
import com.patronat.top.ui.atoms.inputs.InputState
import com.patronat.top.ui.atoms.inputs.InputStyles
import com.patronat.top.ui.models.SelectGroup
import com.patronat.top.ui.models.SelectOption
import com.patronat.top.ui.theme.AccentPink
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.BodyLarge
import com.patronat.top.ui.theme.BodyMedium
import com.patronat.top.ui.theme.InputPlaceholderText
import com.patronat.top.ui.theme.InputLabelText
import com.patronat.top.ui.theme.InputTextContent
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft200
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft50
import com.patronat.top.ui.theme.MineShaft600
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.Primary100
import com.patronat.top.ui.theme.Primary400
import com.patronat.top.ui.theme.Primary600
import com.patronat.top.ui.theme.White

@Composable
fun AppSelectField(
    selectedOption: SelectOption?,
    options: List<SelectOption>,
    onOptionSelected: (SelectOption) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    labelTextStyle: TextStyle = InputLabelText,
    placeholder: String? = null,
    forceState: InputState? = null,
    enabled: Boolean = true,
    groupedOptions: List<SelectGroup> = emptyList(),
    groupIconProvider: (@Composable (SelectGroup) -> Painter?)? = null,
    searchPlaceholder: String? = null,
    emptyResultsText: String? = null
) {
    var expanded by remember { mutableStateOf(false) }
    var fieldWidthPx by remember { mutableIntStateOf(0) }
    val interactionSource = remember { MutableInteractionSource() }
    val isFocused by interactionSource.collectIsFocusedAsState()

    val currentState = when {
        forceState != null -> forceState
        !enabled -> InputState.Disabled
        expanded || isFocused -> InputState.Active
        selectedOption != null -> InputState.Filled
        else -> InputState.Default
    }
    val styles = getSelectInputStyles(currentState)
    val displayText = selectedOption?.label.orEmpty()
    val hasGroupedOptions = groupedOptions.isNotEmpty()

    Box(modifier = modifier) {
        Column(modifier = Modifier.fillMaxWidth()) {
            if (label != null) {
                InputLabel(
                    text = label,
                    textStyle = labelTextStyle
                )
                Spacer(modifier = Modifier.height(8.dp))
            }

            BasicTextField(
                value = displayText,
                onValueChange = {},
                readOnly = true,
                enabled = enabled,
                textStyle = styles.textColor,
                singleLine = true,
                interactionSource = interactionSource,
                modifier = Modifier.fillMaxWidth(),
                decorationBox = { innerTextField ->
                    Row(
                        modifier = Modifier
                            .height(61.dp)
                            .fillMaxWidth()
                            .background(styles.backgroundColor, RoundedCornerShape(8.dp))
                            .border(styles.borderWidth, styles.borderColor, RoundedCornerShape(8.dp))
                            .clickable(enabled = enabled) { expanded = !expanded }
                            .padding(horizontal = 16.dp)
                            .onGloballyPositioned { fieldWidthPx = it.size.width },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Box(modifier = Modifier.weight(1f)) {
                            if (displayText.isEmpty() && placeholder != null) {
                                Text(
                                    text = placeholder,
                                    style = InputPlaceholderText,
                                    color = InputPlaceholderText.color.takeOrElse { styles.placeholderColor }
                                )
                            }
                            innerTextField()
                        }

                        Icon(
                            painter = if (expanded) AppIcons.ArrowUp else AppIcons.ArrowDown,
                            contentDescription = null,
                            tint = styles.iconColor,
                            modifier = Modifier.size(18.dp)
                        )
                    }
                }
            )
        }

        val fieldWidth = with(LocalDensity.current) { fieldWidthPx.toDp() }
        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            modifier = Modifier
                .width(if (fieldWidthPx == 0) 200.dp else fieldWidth)
                .background(White)
        ) {
            if (hasGroupedOptions) {
                GroupedDropdownContent(
                    groups = groupedOptions,
                    selectedOption = selectedOption,
                    onOptionSelected = { option ->
                        onOptionSelected(option)
                        expanded = false
                    },
                    searchPlaceholder = searchPlaceholder ?: placeholder.orEmpty(),
                    emptyResultsText = emptyResultsText,
                    groupIconProvider = groupIconProvider
                )
            } else {
                options.forEach { option ->
                    DropdownMenuItem(
                        text = { Text(text = option.label, style = BodyLarge, color = MineShaft900) },
                        onClick = {
                            onOptionSelected(option)
                            expanded = false
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun GroupedDropdownContent(
    groups: List<SelectGroup>,
    selectedOption: SelectOption?,
    onOptionSelected: (SelectOption) -> Unit,
    searchPlaceholder: String,
    emptyResultsText: String?,
    groupIconProvider: (@Composable (SelectGroup) -> Painter?)?
) {
    var searchQuery by remember { mutableStateOf("") }
    var expandedGroupId by remember(groups) { mutableStateOf(groups.firstOrNull()?.id) }

    val normalizedQuery = searchQuery.trim()
    val isSearching = normalizedQuery.isNotBlank()
    val visibleGroups = groups.mapNotNull { group ->
        val matchingByName = group.label.contains(normalizedQuery, ignoreCase = true)
        val filteredOptions = if (isSearching) {
            group.options.filter { it.label.contains(normalizedQuery, ignoreCase = true) }
        } else {
            group.options
        }

        when {
            !isSearching -> group to filteredOptions
            matchingByName -> group to group.options
            filteredOptions.isNotEmpty() -> group to filteredOptions
            else -> null
        }
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(8.dp)
            .heightIn(max = 420.dp)
            .verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        SearchInput(
            query = searchQuery,
            onQueryChange = { searchQuery = it },
            placeholder = searchPlaceholder.ifBlank { "Пошук" },
            modifier = Modifier.fillMaxWidth()
        )

        if (visibleGroups.isEmpty()) {
            Text(
                text = emptyResultsText.orEmpty(),
                style = BodyMedium,
                color = MineShaft500,
                modifier = Modifier.padding(horizontal = 8.dp, vertical = 12.dp)
            )
            return@Column
        }

        visibleGroups.forEach { (group, groupOptions) ->
            val isExpanded = if (isSearching) true else expandedGroupId == group.id

            GroupHeaderRow(
                group = group,
                isExpanded = isExpanded,
                icon = groupIconProvider?.invoke(group),
                onClick = {
                    if (!isSearching) {
                        expandedGroupId = if (expandedGroupId == group.id) null else group.id
                    }
                }
            )

            if (isExpanded) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(start = 36.dp, top = 2.dp, end = 8.dp, bottom = 6.dp),
                    verticalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    groupOptions.forEach { option ->
                        val isSelected = selectedOption?.id == option.id
                        CategoryOptionRow(
                            text = option.label,
                            selected = isSelected,
                            onClick = { onOptionSelected(option) }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun GroupHeaderRow(
    group: SelectGroup,
    isExpanded: Boolean,
    icon: Painter?,
    onClick: () -> Unit
) {
    val shape = RoundedCornerShape(12.dp)

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(White, shape)
            .border(1.dp, MineShaft100, shape)
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (icon != null) {
            Box(
                modifier = Modifier
                    .size(26.dp)
                    .background(Primary100, RoundedCornerShape(8.dp)),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = icon,
                    contentDescription = null,
                    tint = MineShaft900,
                    modifier = Modifier.size(14.dp)
                )
            }
            Spacer(modifier = Modifier.width(10.dp))
        }

        Text(
            text = group.label,
            style = BodyMedium,
            color = MineShaft900,
            modifier = Modifier.weight(1f)
        )

        Icon(
            painter = if (isExpanded) AppIcons.ArrowUp else AppIcons.ArrowDown,
            contentDescription = null,
            tint = MineShaft500,
            modifier = Modifier.size(16.dp)
        )
    }
}

@Composable
private fun CategoryOptionRow(
    text: String,
    selected: Boolean,
    onClick: () -> Unit
) {
    Text(
        text = text,
        style = if (selected) BodyLarge.copy(fontWeight = FontWeight.SemiBold) else BodyLarge,
        color = if (selected) MineShaft900 else MineShaft600,
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = 4.dp)
    )
}

@Composable
private fun getSelectInputStyles(state: InputState): InputStyles {
    val baseTextStyle = InputTextContent

    return when (state) {
        InputState.Default -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 0.5.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.Normal, color = MineShaft500),
            placeholderColor = MineShaft500,
            iconColor = Primary600
        )
        InputState.Active -> InputStyles(
            borderColor = Primary600,
            borderWidth = 1.dp,
            backgroundColor = Primary100,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.SemiBold, color = MineShaft900),
            placeholderColor = MineShaft500,
            iconColor = Primary600
        )
        InputState.Filled -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.SemiBold, color = MineShaft900),
            placeholderColor = MineShaft500,
            iconColor = MineShaft900
        )
        InputState.ReadOnly -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.SemiBold, color = MineShaft600),
            placeholderColor = MineShaft600,
            iconColor = MineShaft900
        )
        InputState.Error -> InputStyles(
            borderColor = AccentPink,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.SemiBold, color = MineShaft900),
            placeholderColor = MineShaft500,
            iconColor = AccentPink
        )
        InputState.Success -> InputStyles(
            borderColor = Primary400,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.SemiBold, color = MineShaft900),
            placeholderColor = MineShaft500,
            iconColor = Primary600
        )
        InputState.Disabled -> InputStyles(
            borderColor = MineShaft100,
            borderWidth = 0.5.dp,
            backgroundColor = MineShaft100,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.SemiBold, color = MineShaft600),
            placeholderColor = MineShaft600,
            iconColor = Primary600
        )
    }
}
